import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { documentInfo } from "../components/Document/_utils";
import {
  APP_HEARTBEAT,
  COMPANY_DELETE_AFTER,
  COMPANY_SWITCH_AFTER,
  CUSTOM_FONTS_UPDATE_AFTER,
  DOCUMENT_SAVE_AFTER,
  JOIN_COMPANY_ROOM,
  PUBLISH_DOCUMENT_AFTER,
  PUBLISH_DOCUMENT_DOWNLOAD_AFTER,
  PUBLISH_DOCUMENT_PASSWORD_AFTER,
  SESSION_TERMINATE_AFTER,
  STREAMING_COMPLETE,
  SYNC_MERGE_FIELD_AFTER,
  SYNC_DOCUMENT_DELETE_AFTER,
  SYNC_QUEUE_AFTER,
  SYNC_READ_QUEUE_AFTER,
  SYNC_SIGNATURE_AFTER,
  UNPUBLISH_DOCUMENT_AFTER,
  USER_DEACTIVATE_AFTER,
  USER_DELETE_AFTER,
  USER_INFO_UPDATE_AFTER,
  USER_PROFILE_CHANGE_AFTER,
  THIRDPARTY_INTEGRATION_UPDATE_AFTER,
  MANAGER_USER_DELETE,
  MANAGER_USER_ADD,
  MANAGER_USER_UPDATE
} from "../constants/socket";
import { clearCurrentUserSession, getUserInfo, logout } from "../store/actions/authActions";
import { fetchAllMergeField, fetchCompany, fetchThirdPartyAccount, getCompanyInfo, getConnectedAccounts, getUserList, switchCompany } from "../store/actions/companyActions";
import { removeKeyFromStorage } from "../_helpers/utils";
import { fetchAllCustomFonts } from "../store/actions/resourcesActions";
import { addToQueue, syncReadQueue, updateQueue } from "../store/actions/downloadQueueActions";
import { fetchDocumentMergeFields } from "../store/actions/documentActions";
import { getConnectedProviders } from "../store/actions/integrationActions";

const useAcceptSocketEmit = (webSocket, setIsSaveInProgress, documentWS) => {
  const dispatch = useDispatch();
  const authState = useRef(null);
  // const { profilePermissionInfo = null } = useSelector(state => state?.company || {});

  const authStateInfo = useSelector(state => state?.auth);
  const joinCompany = companyId => {
    const subscribe = { type: JOIN_COMPANY_ROOM, companyId, userId: authState?.current?.uid };
    webSocket.send(JSON.stringify(subscribe));
  };

  const urlRedirection = (url = "/") => {
    window.location.replace(url);
  };

  // const downloadFile = (url, fileName) => {
  //   const iframe = document.createElement("iframe");
  //   iframe.src = "about:blank";
  //   iframe.name = "dh-download";
  //   iframe.style.border = 0;
  //   const link = document.createElement("a");
  //   link.href = url;
  //   link.setAttribute("download", fileName);
  //   link.target = "dh-download";

  //   // Append to html link element page
  //   document.body.appendChild(iframe);
  //   document.body.appendChild(link);

  //   // Start download
  //   link.click();

  //   // Clean up and remove the link
  //   setTimeout(() => {
  //     document.body.removeChild(iframe);
  //     document.body.removeChild(link);
  //   }, 1000 * 3);
  // };

  const switchActiveCompany = (isFetchComp = true) => {
    dispatch(getUserInfo()).then(resp => {
      if (isFetchComp) dispatch(fetchCompany());
      dispatch(switchCompany(resp.userInfo.company.id)).then(() => {
        urlRedirection("/templates");
      });
    });
  };

  const onMessage = useCallback(message => {
    const response = JSON.parse(message?.data);
    switch (response.type) {
      case APP_HEARTBEAT:
        console.log(`Heartbeat --> ${response.status}`);
        break;

      case COMPANY_SWITCH_AFTER:
        dispatch(switchCompany(response.companyId)).then(res => {
          // Disabled redirection for public checkout only
          if (res?.result?.id && window.location.pathname !== "/checkout") {
            dispatch(fetchCompany());
            dispatch(getUserInfo()).then(() => {
              urlRedirection("/templates");
            });
          }
        });
        break;

      case COMPANY_DELETE_AFTER:
        dispatch(fetchCompany()).then(res => {
          if (res.result.success || res.result.company_create) {
            switchActiveCompany(false);
          }
        });
        break;

      case PUBLISH_DOCUMENT_AFTER:
        documentInfo.set(response.data, { publish_id: response.publish_id });
        break;

      case UNPUBLISH_DOCUMENT_AFTER:
        documentInfo.set(response.data, {
          publish_id: response.publish_id,
        });
        documentInfo.set(response.data, {
          password: response.password,
        });
        documentInfo.set(response.data, {
          download: response.download,
        });
        break;

      case PUBLISH_DOCUMENT_PASSWORD_AFTER:
        documentInfo.set(response.data, {
          password: response.password,
        });
        break;

      case PUBLISH_DOCUMENT_DOWNLOAD_AFTER:
        documentInfo.set(response.data, {
          download: response.download,
        });
        break;

      // case STREAMING_SUCCESS:
      //   fileDownload(response.downloadUrl, `${response.fileName}.${response.fileType}`);
      //   break;

      case SESSION_TERMINATE_AFTER:
        dispatch(logout());
        dispatch(clearCurrentUserSession());
        window.location.href = "/";
        break;

      case USER_DEACTIVATE_AFTER:
        let userList = response.users || [];
        if (userList.includes(authState?.current?.uid)) switchActiveCompany();
        break;

      case USER_DELETE_AFTER:
        let deletedUserList = response.users || [];
        if (deletedUserList.includes(authState?.current?.uid)) switchActiveCompany();
        break;

      case DOCUMENT_SAVE_AFTER:
        setTimeout(() => setIsSaveInProgress(false), 1000);
        break;

      case USER_INFO_UPDATE_AFTER:
        dispatch(getUserList());
        dispatch(getUserInfo());
        break;

      case CUSTOM_FONTS_UPDATE_AFTER:
        if (window.location.href.indexOf("/settings/fonts") === -1) dispatch(fetchAllCustomFonts(response.companyId));
        break;

      case USER_PROFILE_CHANGE_AFTER:
        let users = response.users || [];
        if (users.includes(authState?.current?.uid)) {
          dispatch(getUserInfo());
          removeKeyFromStorage("profilePermissions");
        }
        break;

      case SYNC_QUEUE_AFTER:
        dispatch(addToQueue(response));
        break;

      // update Queue in redux all collaborative Users
      case STREAMING_COMPLETE:
        dispatch(updateQueue(response));
        break;

      case SYNC_READ_QUEUE_AFTER:
        dispatch(syncReadQueue(response));
        break;

      case SYNC_SIGNATURE_AFTER:
        if (
          window.location.href.indexOf("/editor/") >= 0 &&
          window.location.href.indexOf(`${response.documentId}`) >= 0
        )
          window.location.reload(false);
        break;

      case SYNC_MERGE_FIELD_AFTER:
        if (window.location.href.indexOf("/editor/") >= 0 && !response.updateMfCountInDb) {
          let documentId = window.location.pathname.split("editor/")[1];
          dispatch(fetchDocumentMergeFields({ documentId, changeBySocket: Date.now() }));
        }

        if (window.location.href.indexOf("/settings/merge-field") >= 0) {
          dispatch(fetchAllMergeField(response.companyId));
          // window.location.reload();
        }
        break;

      case SYNC_DOCUMENT_DELETE_AFTER:
        if (
          (window.location.href.indexOf("/editor/") >= 0 || window.location.href.indexOf("/documents/") >= 0) &&
          window.location.href.indexOf(`${response.documentId}`) >= 0
        )
          urlRedirection("/documents");

        if (window.location.href.indexOf("/documents/") >= 0) {
          const images = document.querySelectorAll("img");
          const matches = Array.from(images).filter(img => img.src.includes(response.documentId));
          if (matches.length > 0) {
            window.location.reload(false);
          }
        }
        break;

      case THIRDPARTY_INTEGRATION_UPDATE_AFTER:
        {
          dispatch(fetchThirdPartyAccount(response.companyId));
          dispatch(getConnectedAccounts());
          dispatch(getConnectedProviders());
        }
        break;

      case MANAGER_USER_DELETE:
        dispatch(getUserInfo()).then(res => {
          if (res.type === "GET_USER_INFO_SUCCESS") {
            if (window.location.href.includes("/administration/")) window.location.href = "/templates";
          }
        });
        break;

      case MANAGER_USER_ADD:
        dispatch(getUserInfo());
        break;

      case MANAGER_USER_UPDATE:
        dispatch(getUserInfo());
        break;

      default:
        break;
    }
  }, []);

  // Below fn for set save process done
  const onMessageDocSave = useCallback(message => {
    const response = JSON.parse(message?.data);
    switch (response.type) {
      case DOCUMENT_SAVE_AFTER:
        setIsSaveInProgress(false);
        break;

      default:
        break;
    }
  }, []);

  // connet to active company collaboration room
  useEffect(() => {
    if (authState?.current?.user?.company?.id && webSocket?.readyState === 1) {
      joinCompany(authState?.current?.user?.company?.id);
    }
  }, [authState?.current?.user?.company?.id, webSocket?.readyState]);

  // msg listner added
  useEffect(() => {
    // Event listner for default websocket
    if (webSocket?.readyState === 1) webSocket.addEventListener("message", onMessage);

    // Event listner for document save websocket
    if (documentWS?.readyState === 1) documentWS.addEventListener("message", onMessageDocSave);

    return () => {
      webSocket?.removeEventListener("message", onMessage);
      documentWS?.removeEventListener("message", onMessageDocSave);
    };
  }, [webSocket?.readyState, documentWS?.readyState]);

  // Set authstate to Ref
  useEffect(() => {
    authState.current = authStateInfo;
  }, [authStateInfo]);
};

export default useAcceptSocketEmit;
